import React, {useEffect, useState, createContext, useContext} from "react";
import {Navigate, Route, Routes, useLocation, useNavigate} from "react-router";
import {setGlobalNavigate} from "./_config/history.config";
import "./App.scss";
import RequireAuth from "./require_auth";
import OauthRedirect from "./oauth_redirect";
import TermsAndConditions from "./terms_and_conditions";
import CeraVeRedirect from "./CeraVeRedirect";
import ExternalVideoPage from "./MainPage/Body/EnrichmentCenter/ExternalVideoPage/external_video_page.jsx"
import {getCurrentBreakpoint} from "./_helpers/misc_functions";
import {fetchStressorCategories} from "./_redux/_slices/stressor_categories_slice";
import {useDispatch, useSelector} from "react-redux";

const AsyncLogin = React.lazy(() => import("./LoginPage"));
const AsyncConfirmPassword = React.lazy(() => import("./LoginPage/ConfirmPasswordPage"));
const AsyncMain = React.lazy(() => import("./MainPage"));
const AsyncAdmin = React.lazy(() => import("./AdminPage"));
const AsyncForgotPassword = React.lazy(() => import("./LoginPage/ForgotPasswordPage"));
const AsyncResetPassword = React.lazy(() => import("./LoginPage/ResetPasswordPage"));
// const AsyncCreateUser = React.lazy(() => import("./LoginPage/CreateUserPage"));
const AsyncRegistration = React.lazy(() => import("./LoginPage/RegistrationPage"));
const AsyncBurnoutAssessment = React.lazy(() => import("./LoginPage/BurnoutAssessment/burnout_assessment"));
const AsyncReview = React.lazy(() => import("./LoginPage/RegistrationPage/review_page"));
const AsyncBilling = React.lazy(() => import("./LoginPage/RegistrationPage/billing_page"))
const AsyncSingleSignOnPage = React.lazy(() => import("./LoginPage/SingleSignOnPage"));
const AsyncResendSurveyPage = React.lazy(() => import("./LoginPage/ResendSurveyPage"));

const WindowSizeContext = createContext(null);

// This component makes the app scroll the window to the top on any navigation
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]); // Runs whenever the route changes

  return null;
};

const AppRoutes = () => (
  <>
    <ScrollToTop/>
    <Routes>
      <Route path="/login" element={<AsyncLogin/>}/>
      <Route path="/forgot-password" element={<AsyncForgotPassword/>}/>
      <Route path="/reset-password" element={<AsyncResetPassword/>}/>
      <Route path="/resend-survey" element={<AsyncResendSurveyPage/>}/>
      <Route path="/confirm-password/:token/:email" element={<AsyncConfirmPassword/>}/>


      <Route path="/register" element={<AsyncRegistration/>}/>
      <Route path="/assessment" element={
        <RequireAuth>
          <AsyncBurnoutAssessment/>
        </RequireAuth>
        }/>
      <Route path="/billing" element={<AsyncBilling/>}/>
      <Route path={"/review_registration"} element={<AsyncReview/>}/>

      {/* redirects to new registration page for older groups*/}
      <Route path="/create-ec-user/ANA" element={<Navigate to="/register?code=ANA" />}/>
      <Route path="/create-ec-user/CPP" element={<Navigate to="/register?code=CPP" />}/>
      <Route path="/create-ec-user/LGH" element={<Navigate to="/register?code=LGH" />}/>
      <Route path="/create-ec-user/CeraVe" element={<CeraVeRedirect/>}/>
      <Route path="/create-ec-user/:org_name?" element={<Navigate to="/register" />}/>

      <Route path={"/oauth"} element={<OauthRedirect/>}/>
      <Route path={"/single-sign-on"} element={<AsyncSingleSignOnPage/>}/>
      <Route path={"/terms-and-conditions"} element={<TermsAndConditions/>}/>
      <Route path={"/external_video_page"} element={<ExternalVideoPage/>}/>
      <Route path="/admin/*"
             element={
               <RequireAuth>
                 <AsyncAdmin/>
               </RequireAuth>
             }
      />
      <Route path={"*"}
             element={
               <RequireAuth>
                 <AsyncMain/>
               </RequireAuth>
             }
      />
    </Routes>
  </>
);

const App = () => {
  const dispatch = useDispatch();
  const { status } = useSelector((state) => state.stressor_categories);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  const navigate = useNavigate();
  useEffect(() => {
    setGlobalNavigate(navigate);
  }, [navigate]); // Ensures the function is set when the component mounts

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
      process.env.NODE_ENV !== 'production' && console.log("Current Bootstrap Breakpoint:", getCurrentBreakpoint());
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    console.log(`FRONTEND VERSION: ${process.env.REACT_APP_COMMIT_ID} (BRANCH: ${process.env.REACT_APP_BRANCH_NAME})`);
    if (process.env.NODE_ENV === "production") {
      fetch(`${process.env.REACT_APP_API_URL}/health_check`)
      .then(response => response.json())
      .then(data => {
        const { commit_id, branch_name } = data.info;
        console.log(`BACKEND VERSION: ${commit_id} (BRANCH: ${branch_name})`);
      })
      .catch(error => console.error('Error fetching backend health check:', error));
    }
  }, []);

  useEffect(() => {
    if (status === 'idle') {
      dispatch(fetchStressorCategories());
    }
  }, [status, dispatch]);

  return (
    <WindowSizeContext.Provider value={windowSize}>
      <AppRoutes />
    </WindowSizeContext.Provider>
  );
};

export const useWindowSize = () => {
  const context = useContext(WindowSizeContext);
  if (context === null) {
    throw new Error('useWindowSize must be used within a WindowSizeProvider');
  }
  return context;
};

export default App;